import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import { Box } from "@mui/material";

const Row = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "clickable" && prop !== "hasLeading" && prop !== "hasTrailing",
})<{ clickable: boolean; hasLeading: boolean; hasTrailing: boolean }>(
  ({ theme, clickable, hasLeading, hasTrailing }) => ({
    display: "grid",
    gridTemplateColumns: [
      hasLeading && theme.spacing(8),
      "minmax(0, 1fr)",
      hasTrailing && "auto",
    ]
      .filter(Boolean)
      .join(" "),
    columnGap: theme.spacing(3),
    alignItems: "center",
    padding: theme.spacing(2.5, 0),
    borderBottom: `1px solid ${theme.palette.border.secondary}`,
    cursor: clickable ? "pointer" : undefined,
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.border.active}`,
      outlineOffset: -2,
    },
  })
);

const Line = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(3),
  minWidth: 0,
}));

const Start = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  minWidth: 0,
}));

interface Props {
  leading?: ReactNode;
  title: ReactNode;
  titleEnd?: ReactNode;
  subtitle?: ReactNode;
  subtitleEnd?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  testId?: string;
}

export const MobileListRow = ({
  leading,
  title,
  titleEnd,
  subtitle,
  subtitleEnd,
  trailing,
  onClick,
  testId,
}: Props) => (
  <Row
    data-testid={testId}
    clickable={Boolean(onClick)}
    hasLeading={leading !== undefined}
    hasTrailing={trailing !== undefined}
    {...(onClick && {
      role: "button",
      tabIndex: 0,
      onClick,
      onKeyDown: (event: React.KeyboardEvent) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      },
    })}
  >
    {leading}
    <Box display="flex" flexDirection="column" gap={1} minWidth={0}>
      <Line>
        <Start>{title}</Start>
        {titleEnd}
      </Line>
      {(subtitle || subtitleEnd) && (
        <Line>
          <Start>{subtitle}</Start>
          {subtitleEnd}
        </Line>
      )}
    </Box>
    {trailing}
  </Row>
);
